import { useState, useEffect, useContext } from "react";
import {
AppBar,
Typography,
Button,
TextField,
Box,
Breadcrumbs,
IconButton,
Link,
Tooltip,
Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { PageDataContext } from "../../utils/common/PageDataContext.tsx";
import { SETTING_API_02, SETTING_API_20 } from "../../api/ApiModel.tsx";
import {
externalgetApi,
externalpostApi,
} from "../../services/externalApis.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";

// Define interfaces
interface Category {
catcode: number;
catname: string;
descrip: string;
maincatcode: string;
maincatname?: string;
}

interface MainCategory {
maincatcode: string;
maincatname: string;
[key: string]: any;
}

interface UserParams {
LOCCODE: string;
STAFFMAPLOCODE: string;
USERNAME: string;
COMPCODE: string;
}

interface UserState {
USER_PARAM?: UserParams;
}

interface LocationState {
selectedItem?: Category;
actionText?: string;
selectedMainCategory?: string;
existingCategories?: Category[];
}

interface FormDetails {
maincat: string;
catName: string;
description: string;
}

interface ErrorState {
maincat?: string;
catName?: string;
description?: string;
}

interface AutocompleteOption {
label: string;
value: string;
}

interface ApiResponse<T> {
data?: T;
[key: string]: any;
}

function CreateCategory(): React.ReactElement {
const theme = useTheme();
const styles = useStyles(theme);
const navigate = useNavigate();
const { setPageData } = useContext(PageDataContext);
const location = useLocation();
const locationState = location.state as LocationState;

const {
selectedItem,
actionText,
selectedMainCategory,
existingCategories = [],
} = locationState || {};
const isEditMode = actionText === "Edit" && !!selectedItem;

const SYS_USER = useSelector((state: any) => state.user) || {};
const { LOCCODE, STAFFMAPLOCODE, USERNAME, COMPCODE } =
(SYS_USER.USER_PARAM as UserParams) || {};

// ---------- STATE ----------
const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
const [formDetails, setFormDetails] = useState<FormDetails>({
maincat: selectedMainCategory || "",
catName: "",
description: "",
});
const [error, setError] = useState<ErrorState>({});
const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
const [alertOpen, setAlertOpen] = useState<boolean>(false);
const [alertType, setAlertType] = useState<
"success" | "error" | "info" | "warning"
>("success");
const [alertMessage, setAlertMessage] = useState<string>("");

// ---------- MAIN CATEGORIES ----------
const fetchMainCategories = async (): Promise<void> => {
try {
const result = await externalgetApi(SETTING_API_20, "get", {
COMPCODE,
LOCCODE: STAFFMAPLOCODE,
STS: CON_STATUS_ACTIVE,
});
const list: MainCategory[] = Array.isArray(result)
? result
: (result as ApiResponse<MainCategory[]>)?.data || [];
setMainCategories(list);

// Set default main category if not provided
if (isEditMode && selectedItem) {
setFormDetails({
maincat: selectedItem.maincatcode,
catName: selectedItem.catname,
description: selectedItem.descrip,
});
}
// If navigating from "Add New"
else if (selectedMainCategory) {
setFormDetails((prev) => ({ ...prev, maincat: selectedMainCategory }));
}
// Fallback: default to first main category
else if (list.length > 0) {
setFormDetails((prev) => ({ ...prev, maincat: list[0].maincatcode }));
}
} catch (err) {
showAlert("Failed to fetch main categories.", "error");
}
};

// ---------- useEffect ----------
useEffect(() => {
setPageData({ title: "Category" });
}, [setPageData]);

useEffect(() => {
fetchMainCategories();
}, [COMPCODE, STAFFMAPLOCODE]);

// ---------- MAIN CATEGORY OPTIONS ----------
const mainCategoryOptions: AutocompleteOption[] = mainCategories.map(
(item) => ({
label: item.maincatname,
value: item.maincatcode,
})
);

// ---------- Handeler ----------
const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
const { name, value } = e.target;
let newValue = value.replace(/\s{2,}/g, " ").replace(/^\s+/g, "");

if (newValue.length === 1 && newValue[0] === " ") return;

// ---------- Validation ----------
if (name === "catName" && newValue.length > 50) {
setError((prev) => ({
...prev,
catName: "Input cannot exceed 50 characters.",
}));
return;
} else if (name === "catName") {
setError((prev) => ({ ...prev, catName: "" }));
}

if (name === "description" && newValue.length > 100) {
setError((prev) => ({
...prev,
description: "Input cannot exceed 100 characters.",
}));
return;
} else if (name === "description") {
setError((prev) => ({ ...prev, description: "" }));
}

setFormDetails((prev) => ({ ...prev, [name]: newValue }));
};

// ---------- ALERT ----------
const showAlert = (
msg: string,
type: "success" | "error" | "info" | "warning"
): void => {
setAlertMessage(msg);
setAlertType(type);
setAlertOpen(true);
};

// ---------- SUBMIT ----------
const handleSubmit = async (): Promise<void> => {
const { maincat, catName, description } = formDetails;

// ---------- validation ----------
if (!maincat || !catName.trim()) {
setError({
maincat: !maincat ? "Main Category is required." : "",
catName: !catName ? "Category Name is required." : "",
});
showAlert("Please Fill All the Required Fields!", "error");
return;
}

// Duplicate check
const isDuplicate = existingCategories.some((item: Category) => {
const sameName =
item.catname.trim().toLowerCase() === catName.trim().toLowerCase();
const sameMainCat = item.maincatcode === maincat;
if (isEditMode && selectedItem) {
return sameName && sameMainCat && item.catcode !== selectedItem.catcode;
}
return sameName && sameMainCat;
});

if (isDuplicate) {
showAlert("Category already exists under this Main Category!", "warning");
return;
}

setIsButtonDisabled(true);

// ---------- Payload ----------
interface Payload {
COMPCODE: string;
LOCCODE: string;
MAINCATCODE: string;
catname: string;
descrip: string;
sts: string;
enuser: string;
MODE: "UPDATE" | "CREATE";
catcode: number;
}

const payload: Payload = {
COMPCODE: COMPCODE || "",
LOCCODE: STAFFMAPLOCODE || "",
MAINCATCODE: maincat,
catname: catName,
descrip: description,
sts: CON_STATUS_ACTIVE,
enuser: USERNAME || "Admin",
MODE: isEditMode ? "UPDATE" : "CREATE",
catcode: isEditMode && selectedItem ? selectedItem.catcode : 0,
};

try {
await externalpostApi(SETTING_API_02, payload);
showAlert(
isEditMode ? "Updated successfully!" : "Created successfully!",
"success"
);
setTimeout(() => navigate("/vCreateCategory"), 1500);
} catch (err) {
showAlert(
isEditMode ? "Failed to update." : "Failed to create.",
"error"
);
} finally {
setIsButtonDisabled(false);
}
};

// ---------- CANCEL ----------
const handleCancel = (): void => {
// Clear any error messages
setError({});
  
// Navigate back to the previous page
navigate("/vCreateCategory");
};

return (
<Box sx={styles.box_add_page_container}>
{/* ---------- BREADCRUMB ---------- */}
<Box sx={styles.box_breadcrumb}>
<Tooltip title="Back to All Categories" arrow>
<IconButton
id="btn_backtoview"
sx={styles.btn_breadcrumb_back}
onClick={() => navigate("/vCreateCategory")}
>
<ArrowBackIcon
id="ico_backtoview"
sx={styles.btn_breadcrumb_back_icon}
/>
</IconButton>
</Tooltip>
<Typography sx={styles.breadcrumb_divider}>|</Typography>
<Breadcrumbs separator="›" sx={styles.breadcrumb}>
<Link
underline="hover"
sx={styles.breadcrumb_link}
component="button"
>
Home
</Link>
<Link
underline="hover"
sx={styles.breadcrumb_link}
component="button"
>
Settings
</Link>
<Typography sx={styles.breadcrumb_link_page}>
{isEditMode ? "Edit Category" : "Create Category"}
</Typography>
</Breadcrumbs>
</Box>

{/* ---------- APPBAR ---------- */}
<AppBar sx={styles.appbar}>
<Typography sx={styles.appbar_title}>
{isEditMode ? "Edit Category" : "Create Category"}
</Typography>
</AppBar>

{/* ---------- FORM ---------- */}
<Box
component="form"
noValidate
autoComplete="off"
sx={{
...styles.add_form_page_container,
maxWidth: "500px",
marginTop: "20px",
padding: 1,
}}
>
<Grid container spacing={2} direction="column">
{/* Main Category */}
<Grid size={{ xs: 12 }}>
{mainCategoryOptions.length > 0 && (
<Autocomplete
id="atc_maincategory"
options={mainCategoryOptions}
getOptionLabel={(option) => option.label || ""}
disableClearable
value={
mainCategoryOptions.find(
(opt) => opt.value === formDetails.maincat
) || null
}
onChange={(_, newValue) => {
setFormDetails((prev) => ({
...prev,
maincat: newValue ? newValue.value : "",
}));
setError((prev) => ({ ...prev, maincat: "" }));
}}
disabled={isEditMode}
renderInput={(params) => (
<TextField
{...params}
label="Main Category"
size="small"
fullWidth
required
error={!!error.maincat}
helperText={error.maincat}
FormHelperTextProps={{
sx: { fontSize: "12px", color: "red" },
}}
/>
)}
/>
)}
</Grid>

{/* Category Name */}
<Grid size={{ xs: 12 }}>
<TextField
id="txt_categoryname"
label="Category Name"
fullWidth
required
size="small"
name="catName"
value={formDetails.catName}
onChange={handleInputChange}
error={!!error.catName}
helperText={error.catName}
FormHelperTextProps={{ sx: { fontSize: "12px", color: "red" } }}
/>
</Grid>

{/* Description */}
<Grid size={{ xs: 12 }}>
<TextField
id="txt_description"
label="Description"
fullWidth
size="small"
name="description"
value={formDetails.description}
onChange={handleInputChange}
error={!!error.description}
helperText={error.description}
FormHelperTextProps={{ sx: { fontSize: "12px", color: "red" } }}
/>
</Grid>
</Grid>
</Box>

{/* ---------- BUTTONS ---------- */}
<Box sx={styles.add_form_page_button_box}>
<Button
id="btn_cancel"
variant="outlined"
sx={styles.btn_secondary}
onClick={handleCancel}
>
Cancel
</Button>
<Button
id="btn_confirm/modify"
variant="contained"
sx={styles.btn_primary}
onClick={handleSubmit}
disabled={isButtonDisabled}
>
{isEditMode ? "Modify" : "Confirm"}
</Button>
</Box>

{/* ---------- SYSTEM ALERT ---------- */}
<SystemAlert
open={alertOpen}
type={alertType}
message={alertMessage}
onClose={() => setAlertOpen(false)}
/>
</Box>
);
}

export default CreateCategory;